import axios from 'axios';
import type { PiniaPluginContext, Store } from 'pinia';

/** Aborts any in-flight request for the action and returns a fresh signal. */
export function abortStoreAction(store: Store, actionName: string): AbortSignal {
  store.abortControllers?.get(actionName)?.abort();
  const controller = new AbortController();
  store.abortControllers?.set(actionName, controller);
  return controller.signal;
}

export function isAbortError(err: unknown): boolean {
  return axios.isCancel(err);
}

export const AbortControllerPlugin = () => {
  return ({ store }: PiniaPluginContext) => {
    store.abortControllers = new Map<string, AbortController>();

    if (process.env.NODE_ENV === 'development') {
      // Track in the devtools
      store._customProperties.add('abortControllers');
    }

    // Hook into the action lifecycle to manage AbortControllers
    store.$onAction(({ after, name }) => {
      // Create a new AbortController for the action if it doesn't exist
      if (!store.abortControllers.get(name)) {
        store.abortControllers.set(name, new AbortController());
      }

      after(() => {
        // Remove the controller if the action was aborted
        if (store.abortControllers.get(name)?.signal.aborted) {
          store.abortControllers.delete(name);
        }
      });
    });

    // Preserve the original $reset method
    const originalReset = store.$reset.bind(store);
    // Override $reset to clear abort controllers
    store.$reset = () => {
      originalReset();
      store.abortControllers.clear();
    };

    // Abort all actions and clear the controllers
    store.$abortAllActions = () => {
      store.abortControllers.forEach((controller) => controller.abort());
      store.abortControllers.clear();
    };
  };
};

declare module 'pinia' {
  export interface PiniaCustomProperties {
    abortControllers: Map<string, AbortController>;
    $abortAllActions: () => void;
  }
}
